/**
 * Cross-cutting constants for the CoPilot subsystem.
 *
 * Lives here (not co-located with each consumer) so the values are
 * documented in ONE place with rationale, instead of being magic
 * numbers sprinkled across hooks. See radar 2026-04-28 — L-2.
 */

/**
 * Debounce window for the persistent guide-state save.
 *
 * 2 seconds picked to balance:
 *   • frequent enough that a user closing the tab loses < ~2s of
 *     work (chat-bubble accumulation, widget answer, etc.);
 *   • infrequent enough that a fast typist or click-streak doesn't
 *     pin the network with a save per keystroke.
 *
 * If you change this, also revisit `WIDGET_SAVE_DEBOUNCE_MS` —
 * widget patches share the same persistence layer and currently
 * use a tighter 500ms window for the multi-step (videos watched,
 * checklist) case where the user expects per-step durability.
 */
export const GUIDE_SESSION_SAVE_DEBOUNCE_MS = 2000;

/**
 * Debounce window for individual widget-state PATCHes (videos
 * watched, checklist progress, multi-page form step). Tighter than
 * the session-level save because a half-completed multi-step
 * widget is more annoying to lose than a few unflushed chat
 * bubbles.
 *
 * Mirror of the constant in `flow-lifecycle/use-widget-state.ts`
 * — kept aligned through this module so a future tuning happens
 * in one place.
 */
export const WIDGET_SAVE_DEBOUNCE_MS = 500;

/**
 * Threshold at which we bump `resumeCount` on the guide-state
 * row. Resumes within this window are coalesced as a heartbeat.
 *
 * 5 minutes picked to be > a tab-refresh / page-reload (~seconds)
 * and < a coffee-break (~minutes) so the metric reflects actual
 * "user came back to a flow" sessions, not refresh-storms. Tunable;
 * the server enforces the same window in
 * `guide-state.repository.markResume()` (the constant lives there).
 */
export const RESUME_IDLE_THRESHOLD_MS = 5 * 60 * 1000;

/**
 * Delay before navigating after the success modal in the journey-
 * builder finalize flow. Long enough for the user to read
 * "Journey created!", short enough that they don't think the page
 * is stuck.
 */
export const JOURNEY_BUILDER_REDIRECT_DELAY_MS = 2000;
